// URL Cell Component - Renders URLs as clickable links with preview

import React, { useState } from 'react';
import { ExternalLink, Image as ImageIcon, File, Eye, X } from 'lucide-react';
import { isValidUrl, isImageUrl, isDocumentUrl, getUrlDisplayName } from '../../utils/urlUtils';
import { COLORS } from '../../constants/theme';

const URLCell = ({ url, displayText = null }) => {
  const [showPreview, setShowPreview] = useState(false);
  const [imageError, setImageError] = useState(false);

  if (!url || !isValidUrl(url)) {
    return <span className="text-gray-500">N/A</span>;
  }

  const displayName = displayText || getUrlDisplayName(url);
  const isImage = isImageUrl(url);
  const isDoc = isDocumentUrl(url);

  const handleClick = (e) => {
    e.preventDefault();
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handlePreviewClick = (e) => {
    e.stopPropagation();
    setShowPreview(true);
  };

  const handleClosePreview = (e) => {
    e.stopPropagation();
    setShowPreview(false);
    setImageError(false);
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <a
        href={url}
        onClick={handleClick}
        target="_blank"
        rel="noopener noreferrer"
        className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1.5 text-sm font-medium break-all"
        title={url}
      >
        <ExternalLink className="w-3.5 h-3.5 flex-shrink-0" />
        <span className="break-all">{displayName}</span>
      </a>
      
      {isImage && (
        <>
          <button
            onClick={handlePreviewClick}
            className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors flex-shrink-0"
            title="Preview image"
          >
            <Eye className="w-3.5 h-3.5" />
          </button>
          
          {showPreview && (
            <div 
              className="fixed inset-0 bg-black bg-opacity-75 z-50 flex items-center justify-center p-4"
              onClick={handleClosePreview}
            >
              <div className="relative max-w-7xl max-h-[90vh] bg-white rounded-lg shadow-2xl overflow-hidden">
                <button
                  onClick={handleClosePreview}
                  className="absolute top-4 right-4 z-10 bg-white rounded-full p-2 shadow-lg hover:bg-gray-100 transition-colors"
                  title="Close preview"
                >
                  <X className="w-5 h-5 text-gray-700" />
                </button>
                <div className="p-4">
                  <div className="flex items-center gap-2 mb-2">
                    <ImageIcon className="w-5 h-5" style={{ color: COLORS.primary }} />
                    <span className="text-sm font-medium text-gray-700 truncate">{displayName}</span>
                  </div>
                  <div className="max-h-[80vh] overflow-auto">
                    {!imageError ? (
                      <img
                        src={url}
                        alt={displayName}
                        className="max-w-full h-auto rounded"
                        onError={() => setImageError(true)}
                        onLoad={() => setImageError(false)}
                      />
                    ) : (
                      <div className="flex flex-col items-center justify-center p-8 bg-gray-50 rounded">
                        <ImageIcon className="w-12 h-12 text-gray-400 mb-2" />
                        <p className="text-sm text-gray-500 mb-4">Failed to load image</p>
                        <a
                          href={url}
                          onClick={handleClick}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-2"
                        >
                          <ExternalLink className="w-4 h-4" />
                          Open in new tab
                        </a>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
      
      {isDoc && (
        <File className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" title="Document" />
      )}
    </div>
  );
};

export default URLCell;

